<template>
    <div>
        <!-- ヘッダー -->
        <div class="mb-4">
            <h2 class="fw-bold text-success mb-1">
                <i class="fa-regular fa-handshake"></i>
                取引先マスタ
            </h2>
        </div>
        <div class="container" v-cloak>
            <simple-search v-model="keyword" @find="find()" @clear="clear()" placeholder="取引先名・フリガナ・イニシャルで検索"></simple-search>
    
            <div class="mb-4">
                <!-- ヘッダーセクション -->
                <div class="d-flex justify-content-between align-items-center mb-3">
                    <h5 class="mb-0 text-secondary">
                        <i class="fa-solid fa-file-csv me-2"></i>データ管理
                    </h5>
                    <button class="btn btn-primary" @click="editClient()">
                        <i class="fa-solid fa-plus me-2"></i>新規作成
                    </button>
                </div>
    
                <div class="row">
                    <div class="col-md-6">
                        <!-- CSV取込カード -->
                        <div class="card border-0 shadow-sm">
                            <div class="card-body p-4">
                                <h6 class="card-title mb-3">
                                    <i class="fa-solid fa-upload me-2 text-primary"></i>
                                    CSVでまとめて追加と更新
                                </h6>
    
                                <div class="row g-3 align-items-end">
                                    <!-- ファイル選択 -->
                                    <div class="col-12">
                                        <label class="form-label text-muted small mb-2">
                                            CSVファイルを選択
                                            （同一の取引先コードの取引先は上書きされます）
                                        </label>
                                        <div class="input-group">
                                            <span class="input-group-text bg-light border-end-0">
                                                <i class="fa-solid fa-file-csv text-muted"></i>
                                            </span>
                                            <input
                                                class="form-control border-start-0"
                                                type="file"
                                                name="file"
                                                accept=".csv,text/csv"
                                                @change="onSelectCsv"
                                                :disabled="csvUploading"
                                            >
                                            <button 
                                                class="btn btn-success flex-fill flex-md-grow-0"
                                                @click="importCsv"
                                                :disabled="!csvFile || csvUploading"
                                            >
                                                <i class="fa-solid fa-upload me-2"></i>
                                                {{ csvUploading ? '取込中...' : '取り込む' }}
                                            </button>
                                            <button 
                                                class="btn btn-outline-secondary"
                                                @click="clearCsv"
                                                :disabled="csvUploading"
                                                title="クリア"
                                            >
                                                <i class="fa-solid fa-xmark"></i>
                                            </button>
                                        </div>
                                        <div v-if="csvFile" class="form-text mt-2">
                                            <i class="fa-solid fa-check-circle text-success me-1"></i>
                                            選択中: {{ csvFile.name }}
                                        </div>
                                    </div>
                                </div>
    
                                <!-- エラーメッセージ -->
                                <div v-if="csvErrors.length" class="alert alert-danger d-flex align-items-start mt-3 mb-0">
                                    <i class="fa-solid fa-circle-exclamation me-2 mt-1"></i>
                                    <div class="flex-grow-1">
                                        <div class="fw-bold mb-2">取込エラー</div>
                                        <ul class="mb-0 ps-3">
                                            <li v-for="(e, i) in csvErrors" :key="i">{{ e }}</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <!-- CSVエクスポート -->
                        <div class="card border-0 shadow-sm">
                            <div class="card-body p-4">
                                <h6 class="card-title mb-3">
                                    <i class="fa-solid fa-download me-2 text-primary"></i>
                                    CSV出力
                                </h6>
    
                                <div class="btn-download d-grid">
                                    <label class="form-label text-muted small mb-2">
                                        検索結果をCSVで出力します。
                                    </label>
                                    <button type="button" class="btn btn-outline-success" @click="exportCsv()">
                                        <i class="fa-solid fa-download me-2"></i>
                                        取引先マスタCSV
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
    
            <div class="sticky mb-3">
                <pagination v-bind="clients" @goto="goto" :showTotal="true"></pagination>
            </div>
            <div class="mb-4">
                <div v-for="v in clients.data" :key="v.id" class="card mb-2">
                    <div class="card-body d-flex align-items-center" :class="{'opacity-50': v.deleted_at}">
                        <button class="btn btn-outline-secondary me-3" @click="editClient(v.id)" :disabled="v.deleted_at">
                            <span class="fa-solid fa-pencil"></span>
                        </button>
                        <div class="flex-grow-1">
                            <!-- 取引先名＋フリガナ -->
                            <div class="d-flex align-items-baseline mb-1">
                                <span class="fs-5 fw-bold">
                                    <ruby>
                                        {{ v.client_name }}
                                        <rt>
                                            {{ v.client_name_kana }}
                                        </rt>
                                    </ruby>
                                </span>
                                <span v-if="v.deleted_at" class="badge bg-danger ms-2">削除済</span>
                            </div>
                            <!-- 取引先コード・登録番号 -->
                            <div class="d-flex flex-wrap align-items-center small">
                                <span class="me-3" style="min-width: 120px;">
                                    <span class="badge bg-secondary" title="取引先コード">取引先コード</span>
                                    <span class="ms-1 fw-monospace" style="letter-spacing: 0.1em; min-width: 6ch; display: inline-block;">
                                        {{ v.client_code || '-' }}
                                    </span>
                                </span>
                                <span class="me-3" v-if="v.invoice_number">
                                    <span class="badge bg-info" title="適格請求書発行事業者の登録番号">登録番号</span>
                                    <span class="ms-1 fw-monospace">{{ v.invoice_number }}</span>
                                </span>
                            </div>
                        </div>
                        <button class="btn ms-3"
                            @click="toggle(v)"
                            :class="{'btn-outline-danger': !v.deleted_at, 'btn-outline-success': v.deleted_at}">
                            <span class="fa-solid fa-trash-can" v-if="!v.deleted_at"> 削除</span>
                            <span class="fa-solid fa-rotate-right" v-else> 復帰</span>
                        </button>
                    </div>
                </div>
            </div>
    
            <!-- 編集モーダル -->
            <clients-editor
                :client_id="client_id"
                @saved="reload"
                @close="closeModal"
            />
        </div>
    </div>
</template>

<style scoped lang="scss">
.btn-download {
    margin-top: 1.1rem;
}
</style>

<script>
import ClientsEditor from './clientsEditor.vue'
import SimpleSearch from 'components/simpleSearch.vue'
import Pagination from 'components/pagination.vue'

export default {
    components: {
        ClientsEditor,
        SimpleSearch,
        Pagination,
    },
    data() {
        return {
            keyword: "",
            page: 1,
            clients: {
                data: [],
                current_page: 1,
                last_page: 1,
                total: 0,
            },
            // CSV取込用
            csvFile: null,
            csvFileName: "",
            csvUploading: false,
            csvErrors: [],
            csvResultMessage: "",
            modals: [],
            client_id: null,
        }
    },
    methods: {
        reload() {
            axios.get('/api/master/clients', {
                params: {
                    keyword: this.keyword,
                    page: this.page,
                }
            }).then(res => {
                this.clients = res.data
            }).catch(err => {Alert.err(err)});
        },
        editClient(id = null) {
            this.client_id = id;
            setTimeout(() => {
                this.modals['clients-editor'].show();
            });
        },
        closeModal(id) {
            this.modals[id]?.hide();
        },
        toggle(v) {
            let action = "削除";
            let dangerMode = true;
            let icon = "error";
            if (v.deleted_at) {
                action = "復帰";
                dangerMode = false;
                icon = "success";
            }
            swal({
                title: `取引先${action}`,
                text: `指定の取引先を${action}します。\nよろしいですか？`,
                icon: icon,
                dangerMode: dangerMode,
                buttons: ["キャンセル", action]
            }).then(confirm => {
                if (confirm) {
                    axios.post(`/api/master/clients/${v.id}/toggle`).then(res => {
                        Alert.msg(res.data);
                    }).catch(err => {
                        Alert.err(err.response);
                    }).finally(() => this.reload());
                }
            });
        },
        exportCsv() {
            location.href = '/api/master/clients/export?keyword=' + encodeURIComponent(this.keyword);
        },
        find() {
            this.page = 1;
            this.reload();
        },
        clear() {
            this.keyword = "";
            this.find();
        },
        goto(p) {
            this.page = p;
            this.reload();
        },
        onSelectCsv(e) {
            const f = e.target.files?.[0] || null;
            if (!f) return;
            this.csvFile = f;
            this.csvFileName = f ? f.name : "";
            this.csvErrors = [];
        },
        clearCsv() {
            this.csvFile = null;
            this.csvFileName = "";
            this.csvErrors = [];
            document.querySelector('input[type="file"]').value = '';
        },
        importCsv() {
            if (!this.csvFile) {
                this.csvErrors = ["CSVファイルを選択してください。"];
                return;
            }

            this.csvUploading = true;

            const form = new FormData();
            form.append('file', this.csvFile);

            axios.post('/api/master/clients/import', form, {
                headers: { 'Content-Type': 'multipart/form-data' }
            }).then(res => {
                this.clearCsv();
                this.reload();
                Alert.msg("取り込みしました");
            })
            .catch(err => {Alert.err(err)})
            .finally(() => {
                this.csvUploading = false;
            });
        },
    },
    mounted() {
        this.modals['clients-editor'] = new bootstrap.Modal(document.getElementById('clients-editor'));
        this.reload()
    }
}
</script>
